/**
 * INKAR Import Page
 *
 * Import workflow for BBSR INKAR data:
 * 1. Select CSV file from server
 * 2. Analyze CSV for indicators
 * 3. Select indicators (multi-select with search)
 * 4. Select years per indicator (toggles)
 * 5. Preview and execute import
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  FileSpreadsheet,
  Database,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Download,
  Calendar,
  ArrowRight,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  MapPin,
  Square,
  CheckSquare,
  ExternalLink,
  Info,
  Home,
  Building2,
  MapPinned,
  Globe,
  Trash2,
  HardDrive,
  Zap,
  X,
  Map as MapIcon,
} from 'lucide-react'
import {
  useInkarSources,
  useInkarAnalyze,
  useInkarExecuteImport,
  useInkarImportStatus,
  useInkarDataSources,
  useInkarStartDownload,
  useInkarDownloadStatus,
  useGV100Editions,
  useInkarCacheStats,
  useInkarDeleteCacheEntry,
  useInkarClearAllCache,
  useRaumgliederungenStatus,
  useRaumgliederungenImport,
} from '../hooks/inkarQueries'
import { cn } from '../../lib/utils'
import type {
  InkarFileInfo,
  InkarIndicatorSummary,
} from '../services/inkarApi'

// ============================================================================
// Types
// ============================================================================

interface IndicatorSelection {
  kuerzel: string
  name: string
  selectedYears: Set<number>
}

// Default INKAR download URL
const DEFAULT_INKAR_URL = 'https://www.bbr-server.de/imagemap/inkar/download/inkar_2025.zip'

// ============================================================================
// Download Section
// ============================================================================

function DownloadSection({
  onDownloadComplete,
  initialUrl = DEFAULT_INKAR_URL,
}: {
  onDownloadComplete: (extractedPath: string | null) => void
  initialUrl?: string
}) {
  const [url, setUrl] = useState(initialUrl)
  const [downloadJobId, setDownloadJobId] = useState<string | null>(null)

  const downloadMutation = useInkarStartDownload()
  const { data: downloadStatus } = useInkarDownloadStatus(downloadJobId, {
    refetchInterval: downloadJobId ? 1000 : false,
  })

  // Check if download completed
  useEffect(() => {
    if (downloadStatus?.status === 'completed') {
      setDownloadJobId(null)
      onDownloadComplete(downloadStatus.extracted_path || null)
    }
  }, [downloadStatus, onDownloadComplete])

  const handleStartDownload = async () => {
    if (!url.trim()) return

    try {
      const response = await downloadMutation.mutateAsync({
        url: url.trim(),
        extract: true,
      })
      setDownloadJobId(response.job_id)
    } catch (error) {
      console.error('Download start failed:', error)
    }
  }

  // Download in progress
  if (downloadJobId && downloadStatus && downloadStatus.status !== 'failed') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-blue-800 mb-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="font-medium">Download lauft...</span>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-blue-600">{downloadStatus.message}</span>
            <span className="font-medium">{downloadStatus.progress}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${downloadStatus.progress}%` }}
            />
          </div>
        </div>

        {downloadStatus.filename && (
          <p className="text-sm text-blue-600 mt-2">Datei: {downloadStatus.filename}</p>
        )}
      </div>
    )
  }

  // Download failed
  if (downloadStatus?.status === 'failed') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-800 mb-2">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Download fehlgeschlagen</span>
        </div>
        <p className="text-sm text-red-700">{downloadStatus.error || 'Unbekannter Fehler'}</p>
        <button
          onClick={() => {
            setDownloadJobId(null)
          }}
          className="mt-3 text-sm text-red-600 hover:underline"
        >
          Erneut versuchen
        </button>
      </div>
    )
  }

  // Download form
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-amber-800 mb-2">INKAR-Daten herunterladen</p>
          <p className="text-sm text-amber-700 mb-3">
            Laden Sie die INKAR-Daten direkt vom BBSR-Server herunter. Die ZIP-Datei wird
            automatisch entpackt.
          </p>

          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.bbr-server.de/..."
              className="flex-1 px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
            />
            <button
              onClick={handleStartDownload}
              disabled={!url.trim() || downloadMutation.isPending}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                url.trim() && !downloadMutation.isPending
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : 'bg-amber-200 text-amber-400 cursor-not-allowed'
              )}
            >
              {downloadMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Herunterladen
            </button>
          </div>

          <p className="text-xs text-amber-600 mt-2">
            Standard-URL:{' '}
            <button
              onClick={() => setUrl(DEFAULT_INKAR_URL)}
              className="underline hover:text-amber-800"
            >
              INKAR 2025
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// File Selector
// ============================================================================

function FileSelector({
  selectedFile,
  onSelect,
  refreshKey,
}: {
  selectedFile: InkarFileInfo | null
  onSelect: (file: InkarFileInfo | null) => void
  refreshKey?: number
}) {
  const { data: sources, isLoading, error, refetch } = useInkarSources()

  // Refetch when refreshKey changes
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      refetch()
    }
  }, [refreshKey, refetch])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-4">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Suche CSV-Dateien...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span>Fehler beim Laden der Dateien</span>
        </div>
        <button
          onClick={() => refetch()}
          className="mt-2 text-sm text-red-600 hover:underline"
        >
          Erneut versuchen
        </button>
      </div>
    )
  }

  if (!sources || sources.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-amber-800">
          <AlertTriangle className="w-5 h-5" />
          <span>Keine CSV-Dateien im DATA/bbsr Verzeichnis gefunden</span>
        </div>
        <p className="text-sm text-amber-600 mt-2">
          Bitte legen Sie die INKAR CSV-Datei im Verzeichnis DATA/bbsr ab.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        <FileSpreadsheet className="w-4 h-4 inline mr-1" />
        INKAR CSV-Datei
      </label>
      <div className="flex gap-2">
        <select
          value={selectedFile?.path || ''}
          onChange={(e) => {
            const file = sources.find((f) => f.path === e.target.value)
            onSelect(file || null)
          }}
          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Datei auswahlen...</option>
          {sources.map((file) => (
            <option key={file.path} value={file.path}>
              {file.filename} ({file.size_display})
            </option>
          ))}
        </select>
        <button
          onClick={() => refetch()}
          className="px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
          title="Liste aktualisieren"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Indicator Selector
// ============================================================================

function IndicatorSelector({
  indicators,
  selections,
  onSelectionsChange,
}: {
  indicators: InkarIndicatorSummary[]
  selections: Map<string, IndicatorSelection>
  onSelectionsChange: (selections: Map<string, IndicatorSelection>) => void
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [bereichFilter, setBereichFilter] = useState<string | null>(null)
  const [hideRsDuplicates, setHideRsDuplicates] = useState(true)
  const [expandedIndicators, setExpandedIndicators] = useState<Set<string>>(new Set())

  // Get unique Bereiche for filter
  const bereiche = useMemo(() => {
    const set = new Set(indicators.map((i) => i.bereich))
    return Array.from(set).sort()
  }, [indicators])

  // Filter indicators
  const filteredIndicators = useMemo(() => {
    return indicators.filter((ind) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        if (
          !ind.name.toLowerCase().includes(searchLower) &&
          !ind.kuerzel.toLowerCase().includes(searchLower)
        ) {
          return false
        }
      }
      // Bereich filter
      if (bereichFilter && ind.bereich !== bereichFilter) {
        return false
      }
      // RS duplicates filter
      if (hideRsDuplicates && ind.has_rs_equivalent) {
        return false
      }
      return true
    })
  }, [indicators, searchTerm, bereichFilter, hideRsDuplicates])

  // Toggle indicator selection
  const toggleIndicator = useCallback(
    (ind: InkarIndicatorSummary) => {
      const newSelections = new Map(selections)
      if (newSelections.has(ind.kuerzel)) {
        newSelections.delete(ind.kuerzel)
      } else {
        // Select with latest year by default
        newSelections.set(ind.kuerzel, {
          kuerzel: ind.kuerzel,
          name: ind.name,
          selectedYears: new Set([ind.max_year]),
        })
      }
      onSelectionsChange(newSelections)
    },
    [selections, onSelectionsChange]
  )

  // Toggle year for indicator
  const toggleYear = useCallback(
    (kuerzel: string, year: number, indicatorInfo: InkarIndicatorSummary) => {
      const newSelections = new Map(selections)
      const existing = newSelections.get(kuerzel)
      if (existing) {
        const newYears = new Set(existing.selectedYears)
        if (newYears.has(year)) {
          newYears.delete(year)
          if (newYears.size === 0) {
            // If no years left, remove the indicator selection
            newSelections.delete(kuerzel)
          } else {
            newSelections.set(kuerzel, { ...existing, selectedYears: newYears })
          }
        } else {
          newYears.add(year)
          newSelections.set(kuerzel, { ...existing, selectedYears: newYears })
        }
      } else {
        // Create new selection with this year
        newSelections.set(kuerzel, {
          kuerzel,
          name: indicatorInfo.name,
          selectedYears: new Set([year]),
        })
      }
      onSelectionsChange(newSelections)
    },
    [selections, onSelectionsChange]
  )

  // Toggle expansion of indicator
  const toggleExpanded = useCallback((kuerzel: string) => {
    setExpandedIndicators((prev) => {
      const next = new Set(prev)
      if (next.has(kuerzel)) {
        next.delete(kuerzel)
      } else {
        next.add(kuerzel)
      }
      return next
    })
  }, [])

  // Select all years for indicator
  const selectAllYears = useCallback(
    (ind: InkarIndicatorSummary) => {
      const newSelections = new Map(selections)
      newSelections.set(ind.kuerzel, {
        kuerzel: ind.kuerzel,
        name: ind.name,
        selectedYears: new Set(ind.years),
      })
      onSelectionsChange(newSelections)
    },
    [selections, onSelectionsChange]
  )

  // Select latest year only
  const selectLatestYear = useCallback(
    (ind: InkarIndicatorSummary) => {
      const newSelections = new Map(selections)
      newSelections.set(ind.kuerzel, {
        kuerzel: ind.kuerzel,
        name: ind.name,
        selectedYears: new Set([ind.max_year]),
      })
      onSelectionsChange(newSelections)
    },
    [selections, onSelectionsChange]
  )

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="flex-1 min-w-64 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Indikator suchen (Name oder Kurzel)..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Bereich Filter */}
        <select
          value={bereichFilter || ''}
          onChange={(e) => setBereichFilter(e.target.value || null)}
          className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Alle Bereiche</option>
          {bereiche.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>

        {/* RS Duplicates Toggle */}
        <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            checked={hideRsDuplicates}
            onChange={(e) => setHideRsDuplicates(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">RS-Duplikate ausblenden</span>
        </label>
      </div>

      {/* Info */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {filteredIndicators.length} Indikatoren
          {selections.size > 0 && (
            <span className="ml-2 text-blue-600 font-medium">
              ({selections.size} ausgewahlt)
            </span>
          )}
        </span>
        {selections.size > 0 && (
          <button
            onClick={() => onSelectionsChange(new Map())}
            className="text-red-600 hover:underline"
          >
            Auswahl zurucksetzen
          </button>
        )}
      </div>

      {/* Indicator List */}
      <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
        {filteredIndicators.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Keine Indikatoren gefunden</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredIndicators.map((ind) => {
              const isSelected = selections.has(ind.kuerzel)
              const selection = selections.get(ind.kuerzel)
              const isExpanded = expandedIndicators.has(ind.kuerzel)

              return (
                <div
                  key={ind.kuerzel}
                  className={cn(
                    'transition-colors',
                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                  )}
                >
                  {/* Main Row */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleIndicator(ind)}
                      className="flex-shrink-0"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>

                    {/* Expand Button */}
                    <button
                      onClick={() => toggleExpanded(ind.kuerzel)}
                      className="flex-shrink-0 p-1 hover:bg-gray-200 rounded"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                          {ind.kuerzel}
                        </span>
                        {ind.has_rs_equivalent && (
                          <span
                            className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
                            title={`RS-Aquivalent: ${ind.rs_equivalent_code}`}
                          >
                            RS
                          </span>
                        )}
                        <span className="text-xs text-gray-500">{ind.bereich}</span>
                      </div>
                      <p className="text-sm text-gray-900 truncate">{ind.name}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>
                          {ind.min_year}-{ind.max_year} ({ind.year_count} Jahre)
                        </span>
                        {selection && (
                          <span className="text-blue-600 font-medium">
                            {selection.selectedYears.size} ausgewahlt
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => selectLatestYear(ind)}
                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                        title="Nur aktuellstes Jahr"
                      >
                        Aktuell
                      </button>
                      <button
                        onClick={() => selectAllYears(ind)}
                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                        title="Alle Jahre"
                      >
                        Alle
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-3 pl-14 space-y-3">
                      {/* Years */}
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Jahre:</p>
                        <div className="flex flex-wrap gap-1">
                          {ind.years.map((year) => {
                            const yearSelected = selection?.selectedYears.has(year)
                            return (
                              <button
                                key={year}
                                onClick={() => toggleYear(ind.kuerzel, year, ind)}
                                className={cn(
                                  'px-2 py-1 text-xs rounded border transition-colors',
                                  yearSelected
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                )}
                              >
                                {year}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      {/* Raumbezuge */}
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Raumbezuge:</p>
                        <div className="flex flex-wrap gap-1">
                          {ind.raumbezuege.map((rb) => (
                            <span
                              key={rb}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                            >
                              <MapPin className="w-3 h-3" />
                              {rb}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Selection Summary
// ============================================================================

function SelectionSummary({
  selections,
}: {
  selections: Map<string, IndicatorSelection>
}) {
  if (selections.size === 0) {
    return null
  }

  // Count total data points
  let totalYears = 0
  selections.forEach((sel) => {
    totalYears += sel.selectedYears.size
  })

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h4 className="font-medium text-blue-900 mb-2">Auswahl-Zusammenfassung</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-blue-600">Indikatoren</span>
          <p className="font-medium text-blue-900">{selections.size}</p>
        </div>
        <div>
          <span className="text-blue-600">Gesamt Jahre</span>
          <p className="font-medium text-blue-900">{totalYears}</p>
        </div>
        <div>
          <span className="text-blue-600">Geschatzte Datenpunkte</span>
          <p className="font-medium text-blue-900">
            {(totalYears * 11500).toLocaleString('de-DE')}+
          </p>
        </div>
      </div>

      {/* Selected Indicators List */}
      <div className="mt-3 pt-3 border-t border-blue-200">
        <p className="text-xs text-blue-600 mb-2">Ausgewahlte Indikatoren:</p>
        <div className="flex flex-wrap gap-1 max-h-24 overflow-auto">
          {Array.from(selections.values()).map((sel) => (
            <span
              key={sel.kuerzel}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white text-blue-800 rounded border border-blue-300"
              title={`${sel.name} (${sel.selectedYears.size} Jahre)`}
            >
              {sel.kuerzel}
              <span className="text-blue-500">({sel.selectedYears.size})</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Import Execution
// ============================================================================

function ImportExecution({
  filePath,
  selections,
  onReset,
}: {
  filePath: string
  selections: Map<string, IndicatorSelection>
  onReset: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [forceUpdate, setForceUpdate] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)

  const executeMutation = useInkarExecuteImport()
  const { data: jobStatus } = useInkarImportStatus(jobId, {
    refetchInterval: jobId ? 1000 : false,
  })

  // Invalidate queries when job completes
  useEffect(() => {
    if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['inkar', 'data-sources'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    }
  }, [jobStatus?.status, queryClient])

  // Load GV100 editions for automatic year mapping
  const { data: editions } = useGV100Editions()

  // Convert selections to API format
  const indicatorsPayload = useMemo(() => {
    const result: Record<string, number[]> = {}
    selections.forEach((sel, kuerzel) => {
      result[kuerzel] = Array.from(sel.selectedYears)
    })
    return result
  }, [selections])

  // Collect all selected years and calculate edition mapping
  const yearMapping = useMemo(() => {
    const allYears = new Set<number>()
    selections.forEach((sel) => {
      sel.selectedYears.forEach((y) => allYears.add(y))
    })
    const sortedYears = Array.from(allYears).sort((a, b) => b - a)

    if (!editions || editions.length === 0) {
      return {
        years: sortedYears,
        maxYear: sortedYears[0] || new Date().getFullYear(),
        editionMap: new Map<number, string>(),
        unmappedYears: [],
        mappedYears: [],
      }
    }

    // Map each INKAR year to the best GV100 edition
    // Direct match: INKAR year = reference_year (Gebietsstand year)
    const editionYears = editions.map((e) => e.reference_year)
    const minEditionYear = Math.min(...editionYears)
    const maxEditionYear = Math.max(...editionYears)

    const editionMap = new Map<number, string>()
    const unmappedYears: number[] = []
    const mappedYears: number[] = []

    for (const year of sortedYears) {
      if (year > maxEditionYear) {
        // Newer than newest edition -> NOT mappable, needs new GV100 import first
        unmappedYears.push(year)
      } else if (year >= minEditionYear && year <= maxEditionYear) {
        // Direct match: find edition with same reference_year
        const matchingEdition = editions.find((e) => e.reference_year === year)
        if (matchingEdition) {
          editionMap.set(year, matchingEdition.edition)
          mappedYears.push(year)
        }
      } else if (year < minEditionYear) {
        // Older than oldest edition -> use oldest edition
        const oldestEdition = editions[editions.length - 1]
        editionMap.set(year, oldestEdition.edition)
        mappedYears.push(year)
      }
    }

    return {
      years: sortedYears,
      maxYear: mappedYears.length > 0 ? Math.max(...mappedYears) : (sortedYears[0] || new Date().getFullYear()),
      editionMap,
      unmappedYears,
      mappedYears,
    }
  }, [selections, editions])

  // Reference year is max(mapped years), which directly matches the GV100 edition reference_year
  const referenceYear = yearMapping.maxYear

  // Check if import is blocked due to unmapped years
  const hasUnmappedYears = yearMapping.unmappedYears.length > 0
  const newestEdition = editions && editions.length > 0 ? editions[0] : null

  const handleExecute = async () => {
    try {
      const response = await executeMutation.mutateAsync({
        file_path: filePath,
        indicators: indicatorsPayload,
        reference_year: referenceYear,
        force_update: forceUpdate,
      })
      if (response.job_id) {
        setJobId(response.job_id)
      }
    } catch (error) {
      console.error('Import failed:', error)
    }
  }

  // Job completed
  if (jobStatus?.status === 'completed' && jobStatus.result) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800 mb-3">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Import abgeschlossen</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-green-600">Gesamt</span>
              <p className="font-medium text-green-900">
                {jobStatus.result.total_records.toLocaleString('de-DE')}
              </p>
            </div>
            <div>
              <span className="text-green-600">Erstellt</span>
              <p className="font-medium text-green-900">
                {jobStatus.result.created.toLocaleString('de-DE')}
              </p>
            </div>
            <div>
              <span className="text-green-600">Aktualisiert</span>
              <p className="font-medium text-green-900">
                {jobStatus.result.updated.toLocaleString('de-DE')}
              </p>
            </div>
            <div>
              <span className="text-green-600">Dauer</span>
              <p className="font-medium text-green-900">
                {jobStatus.result.duration_seconds.toFixed(1)}s
              </p>
            </div>
          </div>

          {jobStatus.result.errors > 0 && (
            <div className="mt-3 text-sm text-red-600">
              {jobStatus.result.errors} Fehler
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Neuer Import
          </button>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            Zum Dashboard
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  // Job failed
  if (jobStatus?.status === 'failed') {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 mb-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Import fehlgeschlagen</span>
          </div>
          <p className="text-sm text-red-700">{jobStatus.message}</p>
          {jobStatus.result?.error_messages && jobStatus.result.error_messages.length > 0 && (
            <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
              {jobStatus.result.error_messages.slice(0, 5).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          )}
        </div>

        <button
          onClick={onReset}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Erneut versuchen
        </button>
      </div>
    )
  }

  // Job running
  if (jobId && jobStatus && (jobStatus.status === 'pending' || jobStatus.status === 'running')) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-blue-800 mb-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="font-medium">Import lauft...</span>
        </div>

        {jobStatus.progress !== null && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-blue-600">{jobStatus.message}</span>
              <span className="font-medium">{jobStatus.progress}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${jobStatus.progress}%` }}
              />
            </div>
          </div>
        )}

        <p className="text-sm text-blue-600 mt-3">Job-ID: {jobId}</p>
      </div>
    )
  }

  // Configuration
  return (
    <div className="space-y-4">
      {/* Warning for unmapped years - blocks import */}
      {hasUnmappedYears && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-800 mb-2">Fehlende GV100-Edition</p>
              <p className="text-sm text-red-700 mb-3">
                Fur die folgenden Jahre existiert keine passende GV100-Edition.
                Bitte importieren Sie zuerst das Gemeindeverzeichnis (GV100) mit dem entsprechenden Gebietsstand.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {yearMapping.unmappedYears.map((year) => (
                  <span
                    key={year}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-800 border border-red-300 rounded font-medium"
                  >
                    {year} → benotigt Edition {year + 1}
                  </span>
                ))}
              </div>
              <p className="text-xs text-red-600">
                Neueste verfugbare Edition: {newestEdition ? `${newestEdition.edition} (Gebietsstand 31.12.${newestEdition.reference_year})` : 'keine'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edition Mapping Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-blue-800 mb-2">Automatische Edition-Zuordnung</p>
            <p className="text-sm text-blue-700 mb-3">
              INKAR Jahr X entspricht dem Gebietsstand 31.12.X und wird der GV100-Edition X+1 zugeordnet.
            </p>

            {yearMapping.mappedYears.length > 0 && editions && editions.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-blue-600">Zuordnung der importierbaren Jahre:</p>
                <div className="flex flex-wrap gap-2">
                  {yearMapping.mappedYears.slice(0, 10).map((year) => {
                    const edition = yearMapping.editionMap.get(year)
                    return (
                      <span
                        key={year}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-blue-200 rounded"
                      >
                        <span className="font-medium">{year}</span>
                        <ArrowRight className="w-3 h-3 text-blue-400" />
                        <span className="text-blue-600">Ed. {edition}</span>
                      </span>
                    )
                  })}
                  {yearMapping.mappedYears.length > 10 && (
                    <span className="text-xs text-blue-500">+{yearMapping.mappedYears.length - 10} weitere</span>
                  )}
                </div>
              </div>
            )}

            {yearMapping.mappedYears.length === 0 && !hasUnmappedYears && (
              <p className="text-sm text-blue-600">Wahlen Sie Indikatoren mit Jahren aus.</p>
            )}

            {(!editions || editions.length === 0) && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>Keine GV100-Editionen gefunden. Bitte importieren Sie zuerst das Gemeindeverzeichnis.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={forceUpdate}
          onChange={(e) => setForceUpdate(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">
          Bestehende Daten uberschreiben (falls vorhanden)
        </span>
      </label>

      <button
        onClick={handleExecute}
        disabled={selections.size === 0 || executeMutation.isPending || hasUnmappedYears || yearMapping.mappedYears.length === 0}
        className={cn(
          'flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors',
          selections.size > 0 && !executeMutation.isPending && !hasUnmappedYears && yearMapping.mappedYears.length > 0
            ? forceUpdate
              ? 'bg-amber-600 text-white hover:bg-amber-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        )}
      >
        {executeMutation.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Wird gestartet...
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            Import starten
          </>
        )}
      </button>

      {executeMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span>Fehler: {executeMutation.error.message}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Data Source Card (Business Card Style - matches Regionalstatistik)
// ============================================================================

// INKAR Bereich (thematic area) mapping - fallback for indicators without category
const bereichLabels: Record<string, string> = {
  'ZOM': 'Erreichbarkeit & Mobilität',
  'LRB': 'Lebensräume & Bevölkerung',
  'EU': 'Europa',
  'SDG': 'Nachhaltigkeitsziele',
}

// Get display label for grouping (subcategory preferred, then category, then bereich)
function getGroupingKey(ind: { category?: string | null; subcategory?: string | null; bereich?: string }): string {
  // Group by subcategory if available (finest level), otherwise by category
  if (ind.subcategory) return ind.subcategory
  if (ind.category) return ind.category
  if (ind.bereich) return bereichLabels[ind.bereich] || ind.bereich
  return 'Sonstige'
}

// Get main category for tag display
function getMainCategory(ind: { category?: string | null; bereich?: string }): string {
  if (ind.category) return ind.category
  if (ind.bereich) return bereichLabels[ind.bereich] || ind.bereich
  return 'Sonstige'
}

// Format subcategory label for display (remove redundant prefix from subcategory name)
function formatSubcategoryLabel(subcategory: string): string {
  // If subcategory contains " - ", extract the part after the dash
  // e.g., "Verkehr und Erreichbarkeit - Pendler" -> "Pendler"
  if (subcategory.includes(' – ')) {
    return subcategory.split(' – ')[1].trim()
  }
  if (subcategory.includes(' - ')) {
    return subcategory.split(' - ')[1].trim()
  }
  return subcategory
}

// Extract short distinctive name from full indicator name
function extractShortName(fullName: string): string {
  // Remove common prefixes to get the distinctive part
  const prefixes = [
    /^Durchschn\.\s+/i,
    /^Durchschnittliche\s+/i,
    /^Pkw-Fahrzeit\s+zu[mr]?\s+nächsten\s+/i,
    /^Lkw-Fahrzeit\s+zu[mr]?\s+nächsten\s+/i,
    /^Fahrzeit\s+zu[mr]?\s+nächsten\s+/i,
    /^Anzahl\s+der?\s+/i,
    /^Zahl\s+der?\s+/i,
    /\s+in\s+(Minuten|Min\.|%|km²|km|Euro|EUR|ha)$/i,
    /\s+in\s+Deutschland$/i,
  ]

  let name = fullName
  for (const prefix of prefixes) {
    name = name.replace(prefix, '')
  }

  // Capitalize first letter
  name = name.charAt(0).toUpperCase() + name.slice(1)

  // Truncate if still too long
  if (name.length > 35) {
    name = name.slice(0, 32) + '...'
  }

  return name
}

// ============================================================================
// Category Card (grouped by category)
// ============================================================================

interface CategoryData {
  categoryKey: string       // Key for grouping (subcategory or category)
  label: string             // Display label for card heading (formatted subcategory or category)
  mainCategory: string      // Main category for tag display
  indicators: Array<{
    code: string
    name: string
    unit: string | null
    kuerzel: string
    bereich?: string
  }>
  years: number[]
  entityLevels: string[]
  entityCounts: Record<string, number>
  totalRecords: number
  dataSources: Array<{
    id: number
    name: string
    importDate: string | null
  }>
}

function CategoryCard({ data }: { data: CategoryData }) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Entity level label mapping
  const entityLevelLabels: Record<string, string> = {
    'municipality': 'Gemeinde',
    'county': 'Kreis',
    'state': 'Land',
    'federal': 'Bund',
    'government_district': 'Reg.Bez.',
    'municipal_association': 'Gem.Verb.',
    'labor_market_region': 'Arbeitsmarkt',
    'spatial_planning_region': 'Raumordnung',
    'metropolitan_region': 'Metropol',
    'major_city_region': 'Großstadtregion',
    'ihk_district': 'IHK-Bezirk',
    'bbsr_middle_area': 'Mittelbereich',
    'county_region': 'Kreisregion',
  }

  // Format years for display
  const yearsDisplay = useMemo(() => {
    const years = data.years
    if (!years || years.length === 0) return null
    if (years.length === 1) return String(years[0])
    const sorted = [...years].sort((a, b) => a - b)
    if (sorted.length === 2) return `${sorted[0]}, ${sorted[1]}`
    const isConsecutive = sorted.every((y, i) => i === 0 || y === sorted[i - 1] + 1)
    if (isConsecutive) return `${sorted[0]}–${sorted[sorted.length - 1]}`
    return `${sorted[0]}–${sorted[sorted.length - 1]} (${sorted.length} Jahre)`
  }, [data.years])

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-medium">
                {data.mainCategory}
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                {data.indicators.length} Indikatoren
              </span>
            </div>
            <h3 className="font-medium text-gray-900 text-lg">
              {data.label}
            </h3>
          </div>
        </div>

        {/* Quick Info */}
        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
          {yearsDisplay && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              <span>Jahr {yearsDisplay}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Database className="w-3.5 h-3.5 text-gray-400" />
            <span>{data.totalRecords.toLocaleString('de-DE')} Werte</span>
          </div>
        </div>
      </div>

      {/* Preview Indicators */}
      <div className="p-4">
        <div className="flex flex-wrap gap-1 mb-3">
          {data.indicators.slice(0, isExpanded ? undefined : 6).map((ind, idx) => (
            <span
              key={idx}
              className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200"
              title={`${ind.name}${ind.unit ? ` (${ind.unit})` : ''} [${ind.kuerzel}]`}
            >
              {extractShortName(ind.name)}
            </span>
          ))}
          {!isExpanded && data.indicators.length > 6 && (
            <button
              onClick={() => setIsExpanded(true)}
              className="text-xs text-blue-600 hover:text-blue-800 px-1.5 py-0.5 hover:bg-blue-50 rounded"
            >
              +{data.indicators.length - 6} weitere anzeigen
            </button>
          )}
        </div>

        {isExpanded && data.indicators.length > 6 && (
          <button
            onClick={() => setIsExpanded(false)}
            className="text-xs text-gray-500 hover:text-gray-700 mb-3"
          >
            ↑ Weniger anzeigen
          </button>
        )}

        {/* Entity Level Tags */}
        {data.entityLevels.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {data.entityLevels.slice(0, 6).map((level) => {
              const count = data.entityCounts?.[level]
              return (
                <span
                  key={level}
                  className={cn(
                    'inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded',
                    level === 'municipality' ? 'bg-green-50 text-green-700 border border-green-200' :
                    level === 'county' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                    level === 'state' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                    level === 'federal' ? 'bg-red-50 text-red-700 border border-red-200' :
                    'bg-gray-50 text-gray-600 border border-gray-200'
                  )}
                >
                  {level === 'municipality' && <Home className="w-3 h-3" />}
                  {level === 'county' && <Building2 className="w-3 h-3" />}
                  {level === 'state' && <MapPinned className="w-3 h-3" />}
                  {level === 'federal' && <Globe className="w-3 h-3" />}
                  {entityLevelLabels[level] || level}
                  {count && <span className="text-[10px] opacity-70">({count.toLocaleString('de-DE')})</span>}
                </span>
              )
            })}
            {data.entityLevels.length > 6 && (
              <span className="text-xs text-gray-400">
                +{data.entityLevels.length - 6} weitere
              </span>
            )}
          </div>
        )}
      </div>

      {/* Footer: Data Sources */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
        Quelle: {[...new Set(data.dataSources.map(ds => ds.name))].join(', ')}
      </div>
    </div>
  )
}

// ============================================================================
// Imported Data Grid (grouped by Category)
// ============================================================================

function ImportedDataGrid({ refreshKey }: { refreshKey: number }) {
  const { data: dataSources, isLoading, refetch } = useInkarDataSources()
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (refreshKey > 0) {
      refetch()
    }
  }, [refreshKey, refetch])

  // Aggregate data by subcategory (or category if no subcategory) across all data sources
  const categoryData = useMemo(() => {
    if (!dataSources || dataSources.length === 0) return []

    const groups: Record<string, CategoryData> = {}

    for (const ds of dataSources) {
      for (const ind of ds.indicators) {
        // Group by subcategory if available, otherwise by category
        const categoryKey = getGroupingKey(ind)
        const mainCategory = getMainCategory(ind)

        if (!groups[categoryKey]) {
          // Format the label for display
          let displayLabel = categoryKey
          if (ind.subcategory) {
            displayLabel = formatSubcategoryLabel(ind.subcategory)
          }

          groups[categoryKey] = {
            categoryKey,
            label: displayLabel,
            mainCategory,
            indicators: [],
            years: [],
            entityLevels: [],
            entityCounts: {},
            totalRecords: 0,
            dataSources: [],
          }
        }

        // Add indicator if not already present
        if (!groups[categoryKey].indicators.find(i => i.code === ind.code)) {
          groups[categoryKey].indicators.push({
            code: ind.code,
            name: ind.name,
            unit: ind.unit,
            kuerzel: ind.kuerzel,
            bereich: ind.bereich,
          })
        }

        // Add years
        for (const year of ds.years_imported) {
          if (!groups[categoryKey].years.includes(year)) {
            groups[categoryKey].years.push(year)
          }
        }

        // Merge entity levels and counts
        for (const level of ds.entity_levels) {
          if (!groups[categoryKey].entityLevels.includes(level)) {
            groups[categoryKey].entityLevels.push(level)
          }
          // Take max count across data sources
          const count = ds.entity_counts[level] || 0
          groups[categoryKey].entityCounts[level] = Math.max(
            groups[categoryKey].entityCounts[level] || 0,
            count
          )
        }
      }

      // Add data source reference to all groups in this source
      const groupKeys = [...new Set(ds.indicators.map(i => getGroupingKey(i)))]
      for (const key of groupKeys) {
        if (groups[key]) {
          if (!groups[key].dataSources.find(d => d.id === ds.id)) {
            groups[key].dataSources.push({
              id: ds.id,
              name: ds.dataset_name,
              importDate: ds.import_date,
            })
          }
          // Estimate records for this group (proportional)
          const indicatorsInGroup = ds.indicators.filter(i => getGroupingKey(i) === key).length
          const totalIndicators = ds.indicators.length
          const recordsImported = Number(ds.records_imported) || 0
          if (totalIndicators > 0 && recordsImported > 0) {
            const proportion = indicatorsInGroup / totalIndicators
            groups[key].totalRecords += Math.round(recordsImported * proportion)
          }
        }
      }
    }

    // Sort by main category first, then by label
    let result = Object.values(groups).sort((a, b) => {
      const catCompare = a.mainCategory.localeCompare(b.mainCategory, 'de')
      if (catCompare !== 0) return catCompare
      return a.label.localeCompare(b.label, 'de')
    })

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter((cat) =>
        cat.label.toLowerCase().includes(term) ||
        cat.mainCategory.toLowerCase().includes(term) ||
        cat.indicators.some(ind =>
          ind.name.toLowerCase().includes(term) ||
          (ind.code && ind.code.toLowerCase().includes(term)) ||
          (ind.kuerzel && ind.kuerzel.toLowerCase().includes(term))
        )
      )
    }

    return result
  }, [dataSources, searchTerm])

  // Count totals for display - must be before early returns to satisfy Rules of Hooks
  const totalIndicators = useMemo(() =>
    categoryData.reduce((sum, c) => sum + c.indicators.length, 0),
    [categoryData]
  )

  const allCategoriesCount = useMemo(() => {
    if (!dataSources) return 0
    const groups = new Set<string>()
    for (const ds of dataSources) {
      for (const ind of ds.indicators) {
        groups.add(getGroupingKey(ind))
      }
    }
    return groups.size
  }, [dataSources])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!dataSources || dataSources.length === 0) {
    return (
      <div className="text-center py-12">
        <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500 mb-2">Keine INKAR-Daten importiert</p>
        <p className="text-sm text-gray-400">
          Wählen Sie oben eine CSV-Datei und Indikatoren zum Importieren aus
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Indikator, Kategorie oder Kurzel suchen..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {searchTerm && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            {categoryData.length} von {allCategoriesCount}
          </span>
        )}
      </div>

      {/* Summary header */}
      <div className="flex items-center gap-4 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
        <span className="font-medium">Gesamt:</span>
        <span>{totalIndicators} Indikatoren</span>
        <span>•</span>
        <span>{categoryData.length} Kategorien</span>
        <span>•</span>
        <span>{dataSources.length} Import(e)</span>
      </div>

      {/* Category cards grid */}
      {categoryData.length === 0 && searchTerm ? (
        <div className="text-center py-8 text-gray-500">
          Keine Ergebnisse fur "{searchTerm}"
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {categoryData.map((data) => (
            <CategoryCard key={data.categoryKey} data={data} />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Cache Management
// ============================================================================

function CacheManagement() {
  const { data: cacheStats, isLoading, refetch } = useInkarCacheStats()
  const deleteMutation = useInkarDeleteCacheEntry()
  const clearAllMutation = useInkarClearAllCache()

  const handleDeleteEntry = async (cacheKey: string) => {
    try {
      await deleteMutation.mutateAsync(cacheKey)
    } catch (error) {
      console.error('Failed to delete cache entry:', error)
    }
  }

  const handleClearAll = async () => {
    if (window.confirm('Alle INKAR-Cache-Eintrage loschen?')) {
      try {
        await clearAllMutation.mutateAsync()
      } catch (error) {
        console.error('Failed to clear cache:', error)
      }
    }
  }

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Format TTL to days/hours
  const formatTTL = (seconds: number): string => {
    if (seconds <= 0) return 'Abgelaufen'
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    if (days > 0) return `${days}d ${hours}h`
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  // Extract filename from path
  const extractFilename = (path: string): string => {
    const parts = path.split('/')
    return parts[parts.length - 1] || path
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-4">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Lade Cache-Status...</span>
      </div>
    )
  }

  if (!cacheStats) {
    return null
  }

  // Redis not connected
  if (!cacheStats.connected) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-amber-800">
          <AlertTriangle className="w-5 h-5" />
          <span>Redis-Cache nicht verbunden</span>
        </div>
        <p className="text-sm text-amber-600 mt-1">
          CSV-Analysen werden nicht gecacht. Jede Analyse wird die Datei neu einlesen.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <HardDrive className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Analyse-Cache</h3>
            <p className="text-sm text-gray-500">
              {cacheStats.total_entries} Eintrag(e) • {formatBytes(cacheStats.total_size_bytes)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
            title="Aktualisieren"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {cacheStats.entries.length > 0 && (
            <button
              onClick={handleClearAll}
              disabled={clearAllMutation.isPending}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              {clearAllMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Alle loschen
            </button>
          )}
        </div>
      </div>

      {/* Cache Entries */}
      {cacheStats.entries.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <Zap className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>Kein Cache vorhanden</p>
          <p className="text-sm text-gray-400 mt-1">
            Analysierte CSV-Dateien werden hier gecacht
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {cacheStats.entries.map((entry) => (
            <div
              key={entry.cache_key}
              className="flex items-center justify-between p-4 hover:bg-gray-50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileSpreadsheet className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900 truncate">
                    {extractFilename(entry.file_path)}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>{entry.indicator_count} Indikatoren</span>
                  <span>{entry.total_rows.toLocaleString('de-DE')} Zeilen</span>
                  <span>{formatBytes(entry.size_bytes)}</span>
                  <span className="text-green-600">TTL: {formatTTL(entry.ttl_seconds)}</span>
                  {entry.created_approx && (
                    <span>Erstellt: {entry.created_approx}</span>
                  )}
                </div>
              </div>

              <button
                onClick={() => handleDeleteEntry(entry.cache_key)}
                disabled={deleteMutation.isPending}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Cache-Eintrag loschen"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Info Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Info className="w-3 h-3" />
          <span>Cache wird automatisch nach 7 Tagen geloscht. Bei CSV-Anderungen wird neu analysiert.</span>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Import Dialog
// ============================================================================

type ImportMode = 'download' | 'csv' | 'cache'

function InkarImportDialog({
  isOpen,
  onClose,
  onImportComplete,
  initialDownloadUrl,
}: {
  isOpen: boolean
  onClose: () => void
  onImportComplete: () => void
  initialDownloadUrl?: string
}) {
  const [mode, setMode] = useState<ImportMode>(initialDownloadUrl ? 'download' : 'csv')
  const [selectedFile, setSelectedFile] = useState<InkarFileInfo | null>(null)
  const [selections, setSelections] = useState<Map<string, IndicatorSelection>>(new Map())
  const [sourcesRefreshKey, setSourcesRefreshKey] = useState(0)
  const [pendingExtractedPath, setPendingExtractedPath] = useState<string | null>(null)

  // Fetch sources list
  const { data: sources, refetch: refetchSources } = useInkarSources()

  // Fetch analysis when file is selected
  const { data: analysis, isLoading: isAnalyzing, error: analyzeError } = useInkarAnalyze(
    selectedFile?.path || null
  )

  // Auto-select file after download completes
  useEffect(() => {
    if (pendingExtractedPath && sources && sources.length > 0) {
      const matchingFile = sources.find((f) => f.path.startsWith(pendingExtractedPath))
      if (matchingFile) {
        setSelectedFile(matchingFile)
        setPendingExtractedPath(null)
        setMode('csv') // Switch to CSV mode after download
      }
    }
  }, [pendingExtractedPath, sources])

  const handleDownloadComplete = useCallback((extractedPath: string | null) => {
    setSourcesRefreshKey((k) => k + 1)
    refetchSources()
    if (extractedPath) {
      setPendingExtractedPath(extractedPath)
    }
  }, [refetchSources])

  const handleReset = () => {
    setSelectedFile(null)
    setSelections(new Map())
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleImportSuccess = () => {
    handleReset()
    onImportComplete()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold">Neuer Import</h2>
            <p className="text-sm text-gray-500">INKAR-Daten importieren</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Mode Toggle */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
            <button
              onClick={() => setMode('csv')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                mode === 'csv'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                CSV-Datei
              </div>
            </button>
            <button
              onClick={() => setMode('download')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                mode === 'download'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <div className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Herunterladen
              </div>
            </button>
            <button
              onClick={() => setMode('cache')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                mode === 'cache'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4" />
                Cache
              </div>
            </button>
          </div>

          {/* Download Mode */}
          {mode === 'download' && (
            <DownloadSection onDownloadComplete={handleDownloadComplete} initialUrl={initialDownloadUrl} />
          )}

          {/* Cache Mode */}
          {mode === 'cache' && (
            <CacheManagement />
          )}

          {/* CSV Mode */}
          {mode === 'csv' && (
            <>
              <FileSelector
                selectedFile={selectedFile}
                onSelect={setSelectedFile}
                refreshKey={sourcesRefreshKey}
              />

              {selectedFile && (
                <>
                  {isAnalyzing && (
                    <div className="flex items-center gap-3 text-gray-500 py-4">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Analysiere CSV-Datei (dies kann einige Minuten dauern)...</span>
                    </div>
                  )}

                  {analyzeError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-red-800">
                        <AlertCircle className="w-5 h-5" />
                        <span>Fehler bei der Analyse: {analyzeError.message}</span>
                      </div>
                    </div>
                  )}

                  {analysis && (
                    <>
                      {/* Analysis Summary */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-gray-900">Analyse-Ergebnis</h3>
                          {analysis.from_cache && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                              <Zap className="w-3 h-3" />
                              aus Cache
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Zeilen</span>
                            <p className="font-medium text-gray-900">
                              {analysis.total_rows.toLocaleString('de-DE')}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Indikatoren</span>
                            <p className="font-medium text-gray-900">{analysis.indicator_count}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Jahre</span>
                            <p className="font-medium text-gray-900">
                              {analysis.all_years[0]}-{analysis.all_years[analysis.all_years.length - 1]}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Bereiche</span>
                            <p className="font-medium text-gray-900">{analysis.bereiche.length}</p>
                          </div>
                        </div>
                      </div>

                      {/* Indicator Selection */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-3">Indikatoren auswahlen</h3>
                        <IndicatorSelector
                          indicators={analysis.indicators}
                          selections={selections}
                          onSelectionsChange={setSelections}
                        />
                      </div>

                      {/* Selection Summary */}
                      <SelectionSummary selections={selections} />

                      {/* Import Execution */}
                      {selections.size > 0 && (
                        <div className="border-t border-gray-200 pt-6">
                          <h3 className="font-medium text-gray-900 mb-4">Import starten</h3>
                          <ImportExecution
                            filePath={selectedFile.path}
                            selections={selections}
                            onReset={handleImportSuccess}
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Raumgliederungen Section
// ============================================================================

function RaumgliederungenSection() {
  const { data: statusList, isLoading, refetch } = useRaumgliederungenStatus()
  const importMutation = useRaumgliederungenImport()
  const [importingYear, setImportingYear] = useState<number | null>(null)

  const handleImport = async (filePath: string, referenceYear: number) => {
    setImportingYear(referenceYear)
    try {
      await importMutation.mutateAsync({
        file_path: filePath,
        reference_year: referenceYear,
      })
      refetch()
    } catch (error) {
      console.error('Import failed:', error)
    } finally {
      setImportingYear(null)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Lade Raumgliederungen-Status...</span>
        </div>
      </div>
    )
  }

  if (!statusList || statusList.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gray-100 rounded-lg">
            <MapIcon className="w-6 h-6 text-gray-400" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">BBSR-Raumgliederungen</h3>
            <p className="text-sm text-gray-500 mt-1">
              Keine Raumgliederungen-Dateien gefunden. Laden Sie INKAR-Daten herunter,
              die BBSR_Raumgliederungen_Referenz.xlsx enthält.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <MapIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">BBSR-Raumgliederungen</h3>
            <p className="text-sm text-gray-500">
              Ordnet Gemeinden und Kreise den BBSR-Regionen zu (Metropolregionen, Großstadtregionen, etc.)
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
          title="Aktualisieren"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Status List */}
      <div className="divide-y divide-gray-100">
        {statusList.map((status) => {
          const isImporting = importingYear === status.reference_year
          const hasFile = status.files.length > 0
          const muniPercent = status.total_municipalities > 0
            ? Math.round((status.municipality_mappings / status.total_municipalities) * 100)
            : 0
          const countyPercent = status.total_counties > 0
            ? Math.round((status.county_mappings / status.total_counties) * 100)
            : 0

          return (
            <div key={status.reference_year} className="p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">
                      Gebietsstand {status.reference_year}
                    </span>
                    {status.mapping_complete ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                        <CheckCircle2 className="w-3 h-3" />
                        Vollständig
                      </span>
                    ) : status.municipality_mappings > 0 || status.county_mappings > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                        <AlertTriangle className="w-3 h-3" />
                        Teilweise
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                        Nicht importiert
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-6 mt-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Home className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {status.municipality_mappings.toLocaleString('de-DE')} / {status.total_municipalities.toLocaleString('de-DE')} Gemeinden
                      </span>
                      <span className="text-xs text-gray-400">({muniPercent}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {status.county_mappings.toLocaleString('de-DE')} / {status.total_counties.toLocaleString('de-DE')} Kreise
                      </span>
                      <span className="text-xs text-gray-400">({countyPercent}%)</span>
                    </div>
                  </div>

                  {hasFile && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                      <FileSpreadsheet className="w-3 h-3" />
                      <span>{status.files[0].filename}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {hasFile && (
                    <button
                      onClick={() => handleImport(status.files[0].path, status.reference_year)}
                      disabled={isImporting || importMutation.isPending}
                      className={cn(
                        'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                        isImporting || importMutation.isPending
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : status.mapping_complete
                            ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                      )}
                    >
                      {isImporting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Importiere...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          {status.mapping_complete ? 'Aktualisieren' : 'Importieren'}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Import Result */}
              {importMutation.isSuccess && importMutation.data && importingYear === null && importMutation.variables?.reference_year === status.reference_year && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-800 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>
                      Import erfolgreich: {importMutation.data.municipality_mappings_created + importMutation.data.municipality_mappings_updated} Gemeinde-Mappings,{' '}
                      {importMutation.data.county_mappings_created + importMutation.data.county_mappings_updated} Kreis-Mappings
                      ({importMutation.data.duration_seconds.toFixed(1)}s)
                    </span>
                  </div>
                </div>
              )}

              {importMutation.isError && importMutation.variables?.reference_year === status.reference_year && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-800 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>Import fehlgeschlagen: {importMutation.error.message}</span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function InkarImport() {
  // Query-Parameter (aus der Updates-Seite): ?year=YYYY öffnet den Import-Dialog im Download-Modus mit INKAR-URL des Jahres
  const [searchParams] = useSearchParams()
  const presetYear = /^\d{4}$/.test(searchParams.get('year') ?? '') ? searchParams.get('year') : null
  const presetUrl = presetYear
    ? `https://www.bbr-server.de/imagemap/inkar/download/inkar_${presetYear}.zip`
    : undefined
  const [showImportDialog, setShowImportDialog] = useState(presetUrl !== undefined)
  const [refreshKey, setRefreshKey] = useState(0)

  const handleImportComplete = () => {
    setRefreshKey((k) => k + 1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">INKAR</h1>
          <p className="text-gray-500 mt-1">
            BBSR INKAR-Daten (Indikatoren und Karten zur Raum- und Stadtentwicklung)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://www.inkar.de/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            inkar.de
          </a>
          <button
            onClick={() => setShowImportDialog(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Neuer Import
          </button>
        </div>
      </div>

      {/* Raumgliederungen Section */}
      <RaumgliederungenSection />

      {/* Imported Data */}
      <ImportedDataGrid refreshKey={refreshKey} />

      {/* Import Dialog */}
      <InkarImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImportComplete={handleImportComplete}
        initialDownloadUrl={presetUrl}
      />
    </div>
  )
}
